// src/pages/Users/Techniques/components/TechniquesToolbar.jsx

import React, {
  useMemo,
  useState,
} from "react";

import {
  FaCheck,
  FaChevronDown,
  FaChevronUp,
  FaLayerGroup,
  FaMedal,
  FaRedo,
  FaSearch,
  FaShareAlt,
  FaSlidersH,
  FaSortAmountDown,
} from "react-icons/fa";

import {
  countActiveFilters,
  hasActiveFilters,
} from "../utils/techniquesUtils";

import FilterField from "../ui/FilterField";
import ToolbarIconButton from "../ui/ToolbarIconButton";

export default function TechniquesToolbar({
  dynamicOptions,
  ui,
  searchInput,
  shareCopied,
  filteredCount,
  setSearchInput,
  onChange,
  onReset,
  onShare,
}) {
  const [filtersOpen, setFiltersOpen] =
    useState(false);

  const activeFilters = useMemo(
    () => hasActiveFilters(ui),
    [ui],
  );

  const activeFiltersCount = useMemo(
    () => countActiveFilters(ui),
    [ui],
  );

  return (
    <section className="rounded-[1.75rem] border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-4">
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-slate-400" />

          <input
            type="search"
            value={searchInput}
            onChange={(event) =>
              setSearchInput(
                event.target.value,
              )
            }
            placeholder="Rechercher une technique, une traduction, une famille..."
            className="h-12 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-11 pr-4 text-sm font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-400/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white dark:focus:bg-slate-950"
          />
        </div>

        <ToolbarIconButton
          active={shareCopied}
          onClick={onShare}
          title={
            shareCopied
              ? "Lien copié"
              : "Copier le lien de cette recherche"
          }
        >
          {shareCopied ? (
            <FaCheck />
          ) : (
            <FaShareAlt />
          )}
        </ToolbarIconButton>

        <button
          type="button"
          onClick={() =>
            setFiltersOpen(
              (previousValue) =>
                !previousValue,
            )
          }
          className={`relative inline-flex h-12 items-center justify-center gap-2 rounded-2xl px-4 text-sm font-bold transition active:scale-95 md:hidden ${
            filtersOpen
              ? "bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-200 dark:ring-sky-900"
              : "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
          }`}
        >
          <FaSlidersH />
          <span>Filtres</span>

          {activeFiltersCount > 0 ? (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-black text-white">
              {activeFiltersCount}
            </span>
          ) : null}

          {filtersOpen ? (
            <FaChevronUp className="text-xs" />
          ) : (
            <FaChevronDown className="text-xs" />
          )}
        </button>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 md:hidden">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {filteredCount} résultat
          {filteredCount > 1 ? "s" : ""}
        </p>

        {activeFilters ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition active:scale-95 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaRedo className="text-[11px]" />
            Réinitialiser
          </button>
        ) : null}
      </div>

      <div className="mt-4 hidden grid-cols-3 gap-3 md:grid">
        <FiltersContent
          dynamicOptions={dynamicOptions}
          ui={ui}
          onChange={onChange}
        />
      </div>

      <div className="mt-3 hidden items-center justify-between gap-3 border-t border-slate-200 pt-3 dark:border-slate-800 md:flex">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
          {filteredCount} technique
          {filteredCount > 1 ? "s" : ""} affichée
          {filteredCount > 1 ? "s" : ""}
        </p>

        {activeFilters ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-200 active:scale-95 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
          >
            <FaRedo />
            Réinitialiser les filtres
          </button>
        ) : null}
      </div>

      {filtersOpen ? (
        <div className="mt-3 md:hidden">
          <div className="max-h-[calc(100dvh-190px)] overflow-y-auto rounded-[1.5rem] border border-slate-200 bg-slate-50 p-3 shadow-inner dark:border-slate-800 dark:bg-slate-950">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">
                  Affichage
                </p>

                <h3 className="text-sm font-black text-slate-950 dark:text-white">
                  Affiner les techniques
                </h3>
              </div>

              {activeFilters ? (
                <button
                  type="button"
                  onClick={onReset}
                  className="rounded-full bg-white px-3 py-2 text-xs font-bold text-slate-700 ring-1 ring-slate-200 transition active:scale-95 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700"
                >
                  Reset
                </button>
              ) : null}
            </div>

            <div className="space-y-3">
              <FiltersContent
                dynamicOptions={dynamicOptions}
                ui={ui}
                onChange={onChange}
                stacked
              />
            </div>

            <button
              type="button"
              onClick={() =>
                setFiltersOpen(false)
              }
              className="mt-4 flex min-h-12 w-full items-center justify-center rounded-2xl bg-slate-950 px-4 text-sm font-bold text-white transition active:scale-95 dark:bg-white dark:text-slate-950"
            >
              Voir les résultats
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function FiltersContent({
  dynamicOptions,
  ui,
  onChange,
  stacked = false,
}) {
  const wrapperClass = stacked
    ? "space-y-3"
    : "contents";

  return (
    <div className={wrapperClass}>
      <FilterField
        icon={FaMedal}
        label="Ceinture"
        value={ui.grade}
        options={
          dynamicOptions?.grades || []
        }
        onChange={(value) =>
          onChange({ grade: value })
        }
      />

      <FilterField
        icon={FaLayerGroup}
        label="Famille"
        value={ui.family}
        options={
          dynamicOptions?.families || []
        }
        onChange={(value) =>
          onChange({ family: value })
        }
      />

      <FilterField
        icon={FaSortAmountDown}
        label="Classement"
        value={ui.sort}
        options={[
          {
            value: "beltFamily",
            label:
              "Ceinture › Famille › Nom",
          },
          {
            value: "alpha",
            label:
              "Ordre alphabétique",
          },
          {
            value: "grade",
            label:
              "Trier par ceinture",
          },
          {
            value: "family",
            label:
              "Trier par famille",
          },
        ]}
        onChange={(value) =>
          onChange({ sort: value })
        }
      />
    </div>
  );
}